// Cart Quantity Fix for Shopify
// This script fixes issues with quantity adjustments in the cart page
// It ensures prices update correctly when quantities change

(function() {
  // Configuration
  const DEBUG = true;
  const FORCE_RELOAD = true; // Set to false if you don't want page reloads
  
  // Logging helper - useful for debugging
  function log(message, data) {
    if (!DEBUG) return;
    console.log(`%c Cart Fix | ${message}`, 'background: #0066ff; color: white; padding: 2px 5px;', data || '');
  }

  // Only run on cart page
  function initCartFix() {
    if (!window.location.pathname.includes('/cart')) return;
    log('Initializing Cart Quantity Fix');
    
    // Wait for DOM content to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupCartHandlers);
    } else {
      setupCartHandlers();
    }
  }
  
  function setupCartHandlers() {
    // Find cart form
    const cartForm = document.querySelector('form[action="/cart"]');
    if (!cartForm) {
      log('Cart form not found');
      return;
    }
    
    log('Setting up cart handlers');
    
    // Store original quantities and calculate unit prices
    const lineItems = cartForm.querySelectorAll('.cart-item, tr[id^="CartItem-"]');
    lineItems.forEach(item => {
      const quantityInput = item.querySelector('.quantity__input');
      if (quantityInput) {
        calculateAndStoreUnitPrice(item, quantityInput);
      }
    });
    
    // Intercept quantity button clicks with capture phase to run before Shopify's handlers
    cartForm.addEventListener('click', handleQuantityButtonClick, true);
    
    // Intercept direct input changes
    cartForm.querySelectorAll('.quantity__input').forEach(input => {
      input.addEventListener('change', handleQuantityInputChange);
    });
    
    // Override checkout button
    overrideCheckoutButton(cartForm);
    
    // Override the form submission
    cartForm.addEventListener('submit', handleFormSubmit);
    
    // Add a custom update button if needed
    addCustomUpdateButton(cartForm);
  }
  
  function calculateAndStoreUnitPrice(lineItem, input) {
    // Find the price element
    const priceElements = lineItem.querySelectorAll('.price, .price--end');
    if (!priceElements.length) return;
    
    const priceElement = priceElements[0];
    
    // Get current quantity and price
    const quantity = parseInt(input.value) || 1; // Avoid division by zero
    
    // Extract price from the element
    const priceText = priceElement.textContent.trim();
    const priceMatch = priceText.match(/[\d,.]+/);
    
    if (!priceMatch) return;
    
    const totalPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
    const unitPrice = totalPrice / quantity;
    
    // Store unit price on the line item and input
    lineItem.dataset.unitPrice = unitPrice.toFixed(2);
    input.dataset.lastQuantity = quantity;
    
    log('Calculated unit price', {
      lineItem: lineItem.id,
      totalPrice,
      quantity,
      unitPrice: unitPrice.toFixed(2)
    });
    
    return unitPrice;
  }
  
  function handleQuantityButtonClick(event) {
    // Check if it's a quantity button
    const button = event.target.closest('.quantity__button');
    if (!button) return;
    
    // Get the quantity container and input
    const container = button.closest('.quantity');
    if (!container) return;
    
    const input = container.querySelector('.quantity__input');
    if (!input) return;
    
    // Find the line item
    const lineItem = button.closest('.cart-item, tr[id^="CartItem-"]');
    if (!lineItem) return;
    
    // Determine if it's plus or minus
    const isPlus = button.getAttribute('name') === 'plus' || 
                  button.classList.contains('plus') ||
                  button.getAttribute('aria-label')?.includes('Increase');
    
    // Get current value
    const currentValue = parseInt(input.value) || 0;
    
    // Determine step size
    const step = parseInt(input.getAttribute('step') || input.dataset.step) || 1;
    
    // Calculate new value
    const newValue = isPlus ? currentValue + step : Math.max(0, currentValue - step);
    
    log(`Quantity button clicked: ${isPlus ? 'plus' : 'minus'}`, {
      button,
      currentValue,
      newValue,
      step
    });
    
    // Important: Stop propagation to prevent Shopify's default handlers
    // event.stopPropagation();
    
    // Update input value
    input.value = newValue;
    
    // Mark this input as manually changed
    input.dataset.manuallyChanged = 'true';
    
    // Update the displayed price
    updateDisplayedPrice(lineItem, input, newValue);
    
    // Wait a moment then update cart
    setTimeout(() => {
      forceCartUpdate(lineItem.closest('form'));
    }, 100);
  }
  
  function handleQuantityInputChange(event) {
    const input = event.target;
    const lineItem = input.closest('.cart-item, tr[id^="CartItem-"]');
    if (!lineItem) return;
    
    const newValue = parseInt(input.value) || 0;
    
    log('Quantity input changed', {
      input,
      newValue
    });
    
    // Mark as manually changed
    input.dataset.manuallyChanged = 'true';
    
    // Update price display
    updateDisplayedPrice(lineItem, input, newValue);
    
    // Wait a moment then update cart
    setTimeout(() => {
      forceCartUpdate(lineItem.closest('form'));
    }, 100);
  }
  
  function updateDisplayedPrice(lineItem, input, quantity) {
    // Get unit price (calculate if not already done)
    let unitPrice = parseFloat(lineItem.dataset.unitPrice);
    if (!unitPrice) {
      unitPrice = calculateAndStoreUnitPrice(lineItem, input);
      if (!unitPrice) return;
    }
    
    // Calculate new total price
    const newTotal = unitPrice * quantity;
    
    // Find all price elements for this line item
    const priceElements = lineItem.querySelectorAll('.price, .price--end');
    
    // Update all price elements
    priceElements.forEach(element => {
      // Get the currency symbol
      const currencySymbol = element.textContent.trim().match(/[^\d,.]/)?.[0] || '$';
      element.textContent = `${currencySymbol}${newTotal.toFixed(2)}`;
    });
    
    log('Updated displayed price', {
      lineItem: lineItem.id,
      quantity,
      unitPrice,
      newTotal: newTotal.toFixed(2)
    });
    
    // Also update cart subtotal
    updateCartSubtotal();
  }
  
  function updateCartSubtotal() {
    // Calculate sum of all line items
    let subtotal = 0;
    
    document.querySelectorAll('.cart-item, tr[id^="CartItem-"]').forEach(lineItem => {
      const priceElements = lineItem.querySelectorAll('.price, .price--end');
      if (!priceElements.length) return;
      
      const priceText = priceElements[0].textContent.trim();
      const priceMatch = priceText.match(/[\d,.]+/);
      
      if (priceMatch) {
        subtotal += parseFloat(priceMatch[0].replace(/,/g, ''));
      }
    });
    
    // Update all subtotal elements
    const subtotalElements = document.querySelectorAll('.cart-subtotal__price, .totals__subtotal-value');
    subtotalElements.forEach(element => {
      const currencySymbol = element.textContent.trim().match(/[^\d,.]/)?.[0] || '$';
      element.textContent = `${currencySymbol}${subtotal.toFixed(2)}`;
    });
    
    log('Updated cart subtotal', {
      subtotal: subtotal.toFixed(2)
    });
  }
  
  function forceCartUpdate(form) {
    if (!form) return;
    
    log('Forcing cart update');
    
    // Show loading indicator
    const loadingIndicator = createLoadingIndicator('Updating cart...');
    document.body.appendChild(loadingIndicator);
    
    // Create FormData from the cart form
    const formData = new FormData(form);
    
    // Ensure we're sending the update action
    formData.append('update', '');
    
    // Find all inputs that were manually changed
    const changedInputs = form.querySelectorAll('[data-manually-changed="true"]');
    changedInputs.forEach(input => {
      // Ensure readonly inputs are included (some themes make them readonly)
      if (input.hasAttribute('readonly')) {
        input.removeAttribute('readonly');
        input.dataset.wasReadonly = 'true';
      }
    });
    
    // Use fetch API to update cart
    fetch('/cart/update', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) throw new Error('Cart update failed');
      return response.json();
    })
    .then(data => {
      log('Cart update response', data);
      
      // Restore readonly attributes
      changedInputs.forEach(input => {
        input.removeAttribute('data-manually-changed');
        if (input.dataset.wasReadonly === 'true') {
          input.setAttribute('readonly', 'readonly');
          input.removeAttribute('data-was-readonly');
        }
      });
      
      // Decide whether to reload
      if (FORCE_RELOAD) {
        window.location.reload();
      } else {
        // Remove loading indicator
        if (document.body.contains(loadingIndicator)) {
          document.body.removeChild(loadingIndicator);
        }
      }
    })
    .catch(error => {
      log('Cart update error', error);
      
      // Fallback - reload the page
      window.location.reload();
    });
  }
  
  function createLoadingIndicator(message) {
    const element = document.createElement('div');
    element.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255,255,255,0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    `;
    element.innerHTML = `
      <div style="background: white; padding: 20px; border-radius: 5px; box-shadow: 0 0 10px rgba(0,0,0,0.2);">
        <div>${message}</div>
      </div>
    `;
    return element;
  }
  
  function overrideCheckoutButton(form) {
    const checkoutButtons = form.querySelectorAll('[name="checkout"], .cart__checkout-button');
    
    checkoutButtons.forEach(button => {
      button.addEventListener('click', function(event) {
        // Check if any quantities have been manually changed
        const changedInputs = form.querySelectorAll('[data-manually-changed="true"]');
        
        if (changedInputs.length > 0) {
          // Prevent default checkout
          event.preventDefault();
          event.stopPropagation();
          
          log('Intercepted checkout, updating cart first', {
            changedInputs: changedInputs.length
          });
          
          // Show loading indicator
          const loadingIndicator = createLoadingIndicator('Preparing checkout...');
          document.body.appendChild(loadingIndicator);
          
          // Update cart first
          const formData = new FormData(form);
          formData.append('update', '');
          
          fetch('/cart/update', {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          })
          .then(response => {
            if (!response.ok) throw new Error('Cart update failed');
            return response.json();
          })
          .then(() => {
            // Now proceed to checkout
            window.location.href = '/checkout';
          })
          .catch(() => {
            // Fallback to direct checkout
            window.location.href = '/checkout';
          });
        }
      }, true); // true for capture phase
    });
  }
  
  function handleFormSubmit(event) {
    // If any inputs have been manually changed, we want to handle this ourselves
    const changedInputs = event.target.querySelectorAll('[data-manually-changed="true"]');
    if (changedInputs.length > 0) {
      event.preventDefault();
      forceCartUpdate(event.target);
    }
  }
  
  function addCustomUpdateButton(form) {
    // Check if we need to add a custom update button
    const existingButton = form.querySelector('[name="update"]');
    
    if (!existingButton) {
      // Create a custom update button
      const updateButton = document.createElement('button');
      updateButton.setAttribute('type', 'button');
      updateButton.setAttribute('name', 'update');
      updateButton.classList.add('button', 'button--secondary');
      updateButton.style.cssText = 'margin-top: 10px; display: block;';
      updateButton.textContent = 'Update Cart';
      
      // Add event listener
      updateButton.addEventListener('click', function() {
        forceCartUpdate(form);
      });
      
      // Find a good place to insert it
      const checkoutButton = form.querySelector('[name="checkout"]');
      if (checkoutButton) {
        checkoutButton.parentNode.insertBefore(updateButton, checkoutButton);
      } else {
        // Fallback - append to form
        form.appendChild(updateButton);
      }
    }
  }
  
  // Initialize the script
  initCartFix();
})();